import type { Context } from "./models/context";
import type { RuleHandler } from "./types";
import type { Always } from "types/utilities";

import { checkExpired } from "./rules/checkExpired";
import { checkMinStake } from "./rules/checkMinStake";
import { checkMaxStake } from "./rules/checkMaxStake";
import { checkBaseRequirementType } from "./rules/checkBaseRequirementType";
import { checkExcludedEventTypes } from "./rules/checkExcludedEventTypes";
import { checkExcludePromotionalMarkets } from "./rules/checkExcludePromotionalMarkets";
import { checkExcludeLiveMarkets } from "./rules/checkExcludeLiveMarkets";
import { checkMinimumSelectionOdds } from "./rules/checkMinimumSelectionOdds";
import { checkAllSeasonIdsMustMatch } from "./rules/checkAllSeasonIdsMustMatch";
import { checkAllEventIdsMustMatch } from "./rules/checkAllEventIdsMustMatch";
import { checkMinimumSelections } from "./rules/checkMinimumSelections";
import { checkMaximumSelections } from "./rules/checkMaximumSelections";
import { checkStakeSource } from "./rules/checkStakeSource";
import { checkAllMarketIdsMustMatch } from "./rules/checkAllMarketIdsMustMatch";
import { checkAllSportsMustMatch } from "./rules/checkAllSportsMustMatch";
import { checkAllMarketTypeNamesMustMatch } from "./rules/checkAllMarketTypeNamesMustMatch";
import { checkMinimumOutcomes } from "./rules/checkMinimumOutcomes";
import { checkMaximumOutcomes } from "./rules/checkMaximumOutcomes";
import { checkMinimumExpectedResults } from "./rules/checkMinimumExpectedResults";
import { checkAllMeetingIdsMustMatch } from "./rules/checkAllMeetingIdsMustMatch";
import { checkAllTournamentIdsMustMatch } from "./rules/checkAllTournamentIdsMustMatch";
import { checkAllCompetitorIdsMustMatch } from "./rules/checkAllCompetitorIdsMustMatch";
import { checkAllOutcomeGroupsMustMatch } from "./rules/checkAllOutcomeGroupsMustMatch";
import { checkTimesAllowedToCompletePerEvent } from "./rules/checkTimesAllowedToCompletePerEvent";
import { checkMinimumOdds } from "./rules/checkMinimumOdds";
import { checkExcludedMarketIds } from "./rules/checkExcludedMarketIds";
import { checkExcludedEventIds } from "./rules/checkExcludedEventIds";
import { checkExcludedMeetingIds } from "./rules/checkExcludedMeetingIds";
import { checkExcludedSeasonIds } from "./rules/checkExcludedSeasonIds";
import { checkExcludedTournamentIds } from "./rules/checkExcludedTournamentIds";
import { isTokenRequirementWithUserAttributes } from "@/hooks/firestore/models/tokenAutomation";
import { checkMinimumOutcomeOdds } from "@/utilities/promotions/rules/checkMinimumOutcomeOdds";

type UserDrivenAttributeType = keyof Always<
  Always<Context["automation"]["requirements"]>[number]["userDrivenAttributes"]
>;

const requirementsToRulesMap = new Map<UserDrivenAttributeType, RuleHandler>([
  ["minimumStake", checkMinStake],
  ["maximumStake", checkMaxStake],
  ["excludedEventTypes", checkExcludedEventTypes],
  ["excludePromotionalMarkets", checkExcludePromotionalMarkets],
  ["excludeLiveMarkets", checkExcludeLiveMarkets],
  ["minimumSelectionOdds", checkMinimumSelectionOdds],
  ["minimumSelections", checkMinimumSelections],
  ["maximumSelections", checkMaximumSelections],
  ["tournamentIds", checkAllTournamentIdsMustMatch],
  ["excludedTournamentIds", checkExcludedTournamentIds],
  ["seasonIds", checkAllSeasonIdsMustMatch],
  ["excludedSeasonIds", checkExcludedSeasonIds],
  ["eventIds", checkAllEventIdsMustMatch],
  ["excludedEventIds", checkExcludedEventIds],
  ["marketIds", checkAllMarketIdsMustMatch],
  ["excludedMarketIds", checkExcludedMarketIds],
  ["meetingIds", checkAllMeetingIdsMustMatch],
  ["excludedMeetingIds", checkExcludedMeetingIds],
  ["sports", checkAllSportsMustMatch],
  ["marketTypeNames", checkAllMarketTypeNamesMustMatch],
  ["competitorIds", checkAllCompetitorIdsMustMatch],
  ["outcomeGroups", checkAllOutcomeGroupsMustMatch],
  ["stakeSource", checkStakeSource],
  ["minimumOutcomes", checkMinimumOutcomes],
  ["maximumOutcomes", checkMaximumOutcomes],
  ["minimumExpectedResults", checkMinimumExpectedResults],
  ["timesAllowedToCompletePerEvent", checkTimesAllowedToCompletePerEvent],
  ["minimumOdds", checkMinimumOdds],
  ["minimumOutcomeOdds", checkMinimumOutcomeOdds],
]);

const isAllocationAllowed = (context: Context) => {
  const rules = [checkExpired, checkBaseRequirementType];

  const requirement = (context.automation?.requirements || []).find(
    (requirement) => isTokenRequirementWithUserAttributes(requirement),
  );

  if (!requirement || !requirement.userDrivenAttributes) return false;

  // iterate over every user driven attribute, and find matching rule
  Object.keys(requirement.userDrivenAttributes).forEach((attributeType) => {
    const handler = requirementsToRulesMap.get(
      attributeType as UserDrivenAttributeType,
    );

    if (handler) {
      rules.push(handler);
    }
  });

  return rules.every((handler) => handler(context, requirement));
};

export { isAllocationAllowed };
